import { useConfig } from '../context/ConfigContext';
import { formatMoney } from '../utils/currencyUtil';
import { computeSignupTotal } from '../utils/posSignupUtil';
import { planBillingLabel, planSpanLabel } from '../utils/posLandingUtil';
import { LANDING_BRAND_NAME, LANDING_BRAND_SUFFIX } from '../constants/posLandingConst';

/**
 * The summary at the top of the checkout dialog: the plan, what is included,
 * the add-ons picked on the pricing card, and the total due.
 *
 * The total is worked out by computeSignupTotal — the same function that
 * prices the request when it is saved — so what the visitor is shown is what
 * is recorded.
 *
 * @param {object} props
 * @param {object} props.plan
 * @param {{extraStaff: number, warehouse: number}} props.addons
 * @param {{extraStaffPrice: number, warehousePrice: number}} props.prices
 * @returns {JSX.Element}
 */
function PosLandingOrderSummary({ plan, addons, prices }) {
  const { config } = useConfig();
  const picked = [
    { key: 'staff', label: 'Extra staff account', qty: addons.extraStaff, price: prices.extraStaffPrice },
    { key: 'warehouse', label: 'Warehouse / Stock Room', qty: addons.warehouse, price: prices.warehousePrice },
  ].filter((addon) => plan.allowAddons && addon.qty > 0);
  const { total } = computeSignupTotal(plan, plan.allowAddons ? addons : { extraStaff: 0, warehouse: 0 }, prices);
  const freeStaff = plan.freeStaff || 0;

  return (
    <>
      <div className="sum-card">
        <div className="sum-head">{LANDING_BRAND_NAME} {LANDING_BRAND_SUFFIX} — {plan.name}</div>
        <div className="sum-eyebrow">{planBillingLabel(plan)}</div>
        {plan.isCustom ? (
          <div className="sum-row"><span>{planSpanLabel(plan)}</span><b>By quote</b></div>
        ) : (
          <>
            <div className="sum-row">
              <span>{plan.isTrial ? 'Free trial' : 'Subscription'} ({planSpanLabel(plan)})</span>
              <b>{plan.isTrial ? 'Free' : formatMoney(plan.price, config)}</b>
            </div>
            <div className="sum-row free"><span>1 admin account</span><b>Included</b></div>
            {freeStaff > 0 && (
              <div className="sum-row free">
                <span>{freeStaff} free staff account{freeStaff === 1 ? '' : 's'}</span>
                <b>Included</b>
              </div>
            )}
            {picked.map((addon) => (
              <div className="sum-row" key={addon.key}>
                <span>{addon.label}{addon.qty > 1 ? ` × ${addon.qty}` : ''}</span>
                <b>{formatMoney(addon.price * addon.qty, config)}</b>
              </div>
            ))}
          </>
        )}
      </div>
      {plan.isCustom ? (
        <p className="sum-note" style={{ textAlign: 'left', marginTop: 14 }}>We will send a written quote within one working day.</p>
      ) : (
        <div className="sum-total"><span>Total due today</span><b className="money">{formatMoney(total, config)}</b></div>
      )}
    </>
  );
}

export default PosLandingOrderSummary;
